<template>
  <div
      class="cws-container cws-sidebar-right blog-wrapper"
  >

    <!-- content -->
    <div class="blog-detail-wrapper">
      <b-row>
        <b-col cols="12">
          <b-card>
            <b-overlay
                :show="isLoading"
                rounded="sm"
            >

              <!-- Track info -->
              <div v-if="trackData">
                <h4>
                  Трек
                  <b-button
                      v-if="trackData.artist_id"
                      variant="flat-primary"
                      size="sm"
                      class="ml-50"
                      :to="{name: 'artist-view', params: { id: trackData.artist_id }}"
                  >
                    {{ trackData.artist }}
                    <feather-icon icon="ExternalLinkIcon" size="14" class="ml-25"/>
                  </b-button>
                </h4>
                <b-card class="mb-2" border-variant="primary">
                  <h5>{{ trackData.artist }} — {{ trackData.track }}</h5>
                  <div class="d-flex align-items-center">
                    <span class="mr-2">Прослушивания: {{ formatNumber(trackData.play_count) }}</span>
                    <b-button
                        v-if="trackData.file"
                        variant="success"
                        class="btn-icon"
                        size="sm"
                        :href="trackData.url"
                    >
                      <feather-icon icon="SaveIcon"/>
                    </b-button>
                  </div>
                  <div v-if="trackData.external_ids" class="mt-50">
                    <small class="text-muted mr-50">Внешние ID:</small>
                    <b-badge
                        v-if="trackData.external_ids.spotify"
                        v-b-tooltip.hover
                        :title="'Spotify: ' + trackData.external_ids.spotify"
                        tag="a"
                        :href="'https://open.spotify.com/track/' + trackData.external_ids.spotify"
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="success"
                        class="mr-25 cursor-pointer"
                        style="font-size: 0.7rem;"
                    >SP</b-badge>
                    <b-badge
                        v-if="trackData.external_ids.yandex"
                        v-b-tooltip.hover
                        :title="'Yandex Music: ' + trackData.external_ids.yandex"
                        tag="a"
                        :href="'https://music.yandex.ru/track/' + trackData.external_ids.yandex"
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="danger"
                        class="mr-25 cursor-pointer"
                        style="font-size: 0.7rem;"
                    >YA</b-badge>
                    <b-badge
                        v-if="trackData.external_ids.youtube"
                        v-b-tooltip.hover
                        :title="'YouTube: ' + trackData.external_ids.youtube"
                        tag="a"
                        :href="'https://www.youtube.com/watch?v=' + trackData.external_ids.youtube"
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="danger"
                        class="mr-25 cursor-pointer"
                        style="font-size: 0.7rem; background-color: #ff0000 !important;"
                    >YT</b-badge>
                    <b-badge
                        v-if="trackData.external_ids.second_hand_songs"
                        v-b-tooltip.hover
                        :title="'SHS: ' + trackData.external_ids.second_hand_songs"
                        tag="a"
                        :href="'https://secondhandsongs.com/performance/' + trackData.external_ids.second_hand_songs"
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="info"
                        class="mr-25 cursor-pointer"
                        style="font-size: 0.7rem;"
                    >SH</b-badge>
                  </div>
                </b-card>
              </div>

              <!-- Original -->
              <div v-if="originalData">
                <h4>Оригинал</h4>
                <b-card class="mb-2" border-variant="success">
                  <div class="d-flex align-items-center justify-content-between">
                    <div>
                      <h5 class="mb-0">{{ originalData.artist }} — {{ originalData.track }}</h5>
                      <span>Прослушивания: {{ formatNumber(originalData.play_count) }}</span>
                    </div>
                    <div>
                      <b-button
                          variant="info"
                          class="btn-icon"
                          :to="{name: 'track-covers', params: { id: originalData.id }}"
                      >
                        <feather-icon icon="DiscIcon"/>
                      </b-button>
                      <b-button
                          variant="danger"
                          class="btn-icon ml-1"
                          @click="removeOriginal"
                      >
                        <feather-icon icon="XIcon"/>
                      </b-button>
                    </div>
                  </div>
                </b-card>
              </div>

              <!-- Set original (when no original and no covers) -->
              <div v-if="trackData && !originalData && !(coversData && coversData.length)">
                <h4>Оригинал</h4>
                <b-card class="mb-2" border-variant="secondary">
                  <div class="text-muted mb-1">Оригинал не задан</div>
                  <div class="d-flex align-items-center">
                    <b-form-input
                        v-model="searchOriginalQuery"
                        placeholder="Поиск трека для оригинала..."
                        class="mr-1"
                        @keyup.enter="searchForOriginal"
                    />
                    <b-button
                        variant="primary"
                        class="text-nowrap"
                        @click="searchForOriginal"
                        :disabled="!searchOriginalQuery.trim()"
                    >
                      <feather-icon icon="SearchIcon" class="mr-50"/>
                      <span class="align-middle">Поиск</span>
                    </b-button>
                  </div>
                  <div v-if="searchOriginalResults.length" class="mt-1">
                    <h6>Результаты:</h6>
                    <div
                        v-for="result in searchOriginalResults"
                        :key="result.id"
                        class="d-flex align-items-center justify-content-between mt-1"
                    >
                      <span>{{ result.artist }} — {{ result.track }}
                        <small class="text-muted">[{{ formatNumber(result.play_count) }}]</small>
                      </span>
                      <b-button
                          variant="success"
                          class="btn-icon ml-1"
                          size="sm"
                          @click="setOriginal(result.id)"
                      >
                        <feather-icon icon="CheckIcon"/>
                      </b-button>
                    </div>
                  </div>
                </b-card>
              </div>

              <!-- Covers table -->
              <div v-if="coversData && !originalData">
                <h4>Каверы [{{ coversData.length }}]</h4>
                <b-table
                    :items="coversData"
                    responsive
                    :fields="tableColumns"
                    primary-key="id"
                    show-empty
                    empty-text="Нет каверов"
                    class="position-relative"
                >

                  <template #cell(play_count)="data">
                    <span class="text-nowrap">
                      {{ formatNumber(data.item.play_count) }}
                    </span>
                  </template>

                  <template #cell(actions)="data">
                    <span class="text-nowrap">
                      <b-button
                          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                          variant="info"
                          class="btn-icon"
                          :to="{name: 'track-covers', params: { id: data.item.id }}"
                      >
                        <feather-icon icon="DiscIcon"/>
                      </b-button>
                      <b-button
                          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                          variant="warning"
                          class="btn-icon ml-1"
                          :to="{name: 'track-search', params: { id: data.item.id }}"
                      >
                        <feather-icon icon="DownloadIcon"/>
                      </b-button>
                      <b-button
                          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                          variant="danger"
                          class="btn-icon ml-1"
                          @click="removeCover(data.item.id)"
                      >
                        <feather-icon icon="XIcon"/>
                      </b-button>
                    </span>
                  </template>
                </b-table>
              </div>

              <!-- Add cover -->
              <div v-if="trackData && !originalData" class="mt-2">
                <h4>Добавить кавер</h4>
                <div class="d-flex align-items-center">
                  <b-form-input
                      v-model="searchCoverQuery"
                      placeholder="Поиск трека для кавера..."
                      class="mr-1"
                      @keyup.enter="searchForCover"
                  />
                  <b-button
                      variant="primary"
                      class="text-nowrap"
                      @click="searchForCover"
                      :disabled="!searchCoverQuery.trim()"
                  >
                    <feather-icon icon="SearchIcon" class="mr-50"/>
                    <span class="align-middle">Поиск</span>
                  </b-button>
                </div>
                <div v-if="searchCoverResults.length" class="mt-1">
                  <h6>Результаты:</h6>
                  <div
                      v-for="result in searchCoverResults"
                      :key="result.id"
                      class="d-flex align-items-center justify-content-between mt-1"
                  >
                    <span>{{ result.artist }} — {{ result.track }}
                      <small class="text-muted">[{{ formatNumber(result.play_count) }}]</small>
                    </span>
                    <b-button
                        variant="success"
                        class="btn-icon ml-1"
                        size="sm"
                        @click="addCover(result.id)"
                    >
                      <feather-icon icon="PlusIcon"/>
                    </b-button>
                  </div>
                </div>
              </div>

              <!-- SHS Covers -->
              <div v-if="trackData && trackData.external_ids && trackData.external_ids.second_hand_songs != null" class="mt-2">
                <h4>
                  SHS Каверы
                  <b-button
                      variant="outline-primary"
                      size="sm"
                      class="ml-1"
                      @click="fetchShsCovers"
                  >
                    <feather-icon icon="DownloadCloudIcon" class="mr-50"/>
                    <span class="align-middle">Загрузить из SHS</span>
                  </b-button>
                </h4>
                <div v-if="shsCoversError" class="text-danger mb-1">{{ shsCoversError }}</div>
                <b-table
                    v-if="shsCovers.length"
                    :items="shsCovers"
                    responsive
                    :fields="shsTableColumns"
                    primary-key="shs_id"
                    show-empty
                    empty-text="Нет каверов"
                    class="position-relative"
                    :tbody-tr-class="shsRowClass"
                >
                  <template #cell(index)="data">
                    {{ data.index + 1 }}
                  </template>

                  <template #cell(performer)="data">
                    <div class="d-flex align-items-center">
                      <b-link
                          v-if="data.item.performer.id"
                          :to="{name: 'artist-view', params: { id: data.item.performer.id }}"
                      >
                        {{ data.item.performer.name }}
                      </b-link>
                      <span v-else>
                        {{ data.item.performer.name }}
                        <b-button
                            v-b-tooltip.hover
                            :title="'Найти артиста: ' + data.item.performer.name"
                            variant="outline-warning"
                            class="btn-icon ml-50"
                            size="sm"
                            :to="{name: 'artists-search', query: { q: data.item.performer.name }}"
                        >
                          <feather-icon icon="SearchIcon"/>
                        </b-button>
                      </span>
                      <b-button
                          v-if="data.item.performer.id && !data.item.track_id && !isCoverLinked(data.item.track_id)"
                          v-b-tooltip.hover
                          :title="'Добавить трек: ' + data.item.title"
                          variant="success"
                          class="btn-icon ml-50"
                          size="sm"
                          @click="addTrackAndCover(data.item)"
                      >
                        <feather-icon icon="PlusCircleIcon"/>
                      </b-button>
                    </div>
                  </template>

                  <template #cell(is_original)="data">
                    <b-badge :variant="data.item.is_original ? 'success' : 'warning'">
                      {{ data.item.is_original ? 'Оригинал' : 'Кавер' }}
                    </b-badge>
                  </template>

                  <template #cell(track_id)="data">
                    <template v-if="data.item.track_id">
                      <b-button
                          v-if="!isCurrentTrack(data.item.track_id)"
                          variant="primary"
                          class="btn-icon"
                          size="sm"
                          :to="{name: 'track-covers', params: { id: data.item.track_id }}"
                      >
                        <feather-icon icon="LinkIcon"/>
                      </b-button>
                      <b-button
                          v-if="!isCoverLinked(data.item.track_id) && !isCurrentTrack(data.item.track_id) && !data.item.is_original"
                          variant="success"
                          class="btn-icon ml-50"
                          size="sm"
                          @click="addCover(data.item.track_id)"
                      >
                        <feather-icon icon="PlusIcon"/>
                      </b-button>
                      <b-button
                          v-if="data.item.is_original && !isCurrentTrack(data.item.track_id) && (!originalData || originalData.id !== data.item.track_id)"
                          v-b-tooltip.hover
                          title="Установить как оригинал"
                          variant="success"
                          class="btn-icon ml-50"
                          size="sm"
                          @click="setOriginal(data.item.track_id)"
                      >
                        <feather-icon icon="PlusIcon"/>
                      </b-button>
                      <b-badge
                          v-if="isCurrentTrack(data.item.track_id)"
                          variant="primary"
                      >текущий</b-badge>
                    </template>
                    <span v-else class="text-muted">—</span>
                  </template>

                  <template #cell(uri)="data">
                    <b-button
                        variant="flat-primary"
                        class="btn-icon"
                        size="sm"
                        :href="data.item.uri"
                        target="_blank"
                    >
                      <feather-icon icon="ExternalLinkIcon"/>
                    </b-button>
                  </template>
                </b-table>
              </div>

              <div v-if="!trackData && !isLoading" class="text-center text-muted py-2">
                Данные не найдены
              </div>

            </b-overlay>
          </b-card>
        </b-col>
      </b-row>
    </div>
    <!--/ content -->

  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BOverlay,
  BTable,
  BFormInput,
  BBadge,
  BLink,
  VBTooltip,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import {formatNumber} from '@core/utils/filter'
import router from "@/router";
import store from "@/store";
import musicStoreModule from "@/views/db-music/musicStoreModule";
import {onUnmounted, ref} from "@vue/composition-api";
import {useToast} from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BOverlay,
    BTable,
    BFormInput,
    BBadge,
    BLink,
  },
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },
  setup() {
    const STORE_MODULE_NAME = 'artists'
    const toast = useToast()

    // Register module
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, musicStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const trackData = ref(null)
    const originalData = ref(null)
    const coversData = ref(null)
    const isLoading = ref(false)

    const searchOriginalQuery = ref('')
    const searchOriginalResults = ref([])
    const searchCoverQuery = ref('')
    const searchCoverResults = ref([])
    const shsCovers = ref([])
    const shsCoversError = ref('')

    const tableColumns = [
      {key: 'id', label: '#', sortable: false},
      {key: 'artist', sortable: true},
      {key: 'track', sortable: true},
      {key: 'play_count', sortable: true},
      {key: 'actions'},
    ]

    const shsTableColumns = [
      { key: 'index', label: '#', sortable: false },
      { key: 'performer', label: 'Исполнитель', sortable: true },
      { key: 'is_original', label: 'Тип', sortable: true },
      { key: 'track_id', label: 'В БД', sortable: false },
      { key: 'uri', label: 'SHS', sortable: false },
    ]

    const isCoverLinked = (id) => {
      if (!id || !coversData.value) return false
      return coversData.value.some(c => c.id === id)
    }

    const isCurrentTrack = (id) => {
      return id && String(id) === String(trackId())
    }

    const shsRowClass = (item) => {
      if (item && isCurrentTrack(item.track_id)) {
        return 'table-primary'
      }
      return ''
    }

    const trackId = () => router.currentRoute.params.id

    const fetchShsCovers = async () => {
      isLoading.value = true
      shsCoversError.value = ''
      try {
        const res = await store.dispatch('artists/fetchTrackShsCovers', {id: trackId()})
        if (res.data.success) {
          shsCovers.value = res.data.covers
        } else {
          shsCoversError.value = res.data.message
        }
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка загрузки SHS каверов',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoading.value = false
      }
    }

    const fetchCovers = async () => {
      isLoading.value = true
      try {
        const res = await store.dispatch('artists/fetchTrackCovers', {id: trackId()})
        trackData.value = res.data.track
        originalData.value = res.data.original
        coversData.value = res.data.covers
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка загрузки каверов',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoading.value = false
      }
    }

    const searchForOriginal = async () => {
      const q = searchOriginalQuery.value.trim()
      if (!q) return
      isLoading.value = true
      try {
        const excludeArtistId = trackData.value ? trackData.value.artist_id : null
        const res = await store.dispatch('artists/searchTracksInDb', {q, exclude_id: excludeArtistId})
        searchOriginalResults.value = res.data.data
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка поиска треков',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoading.value = false
      }
    }

    const setOriginal = async (originalId) => {
      isLoading.value = true
      try {
        await store.dispatch('artists/setTrackOriginal', {id: trackId(), original_id: originalId})
        searchOriginalQuery.value = ''
        searchOriginalResults.value = []
        await fetchCovers()
        toast({
          component: ToastificationContent,
          props: {
            title: 'Оригинал установлен',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка установки оригинала',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoading.value = false
      }
    }

    const removeOriginal = async () => {
      isLoading.value = true
      try {
        await store.dispatch('artists/removeTrackOriginal', {id: trackId()})
        await fetchCovers()
        toast({
          component: ToastificationContent,
          props: {
            title: 'Оригинал удалён',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка удаления оригинала',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoading.value = false
      }
    }

    const searchForCover = async () => {
      const q = searchCoverQuery.value.trim()
      if (!q) return
      isLoading.value = true
      try {
        const excludeArtistId = trackData.value ? trackData.value.artist_id : null
        const res = await store.dispatch('artists/searchTracksInDb', {q, exclude_id: excludeArtistId})
        searchCoverResults.value = res.data.data
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка поиска треков',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoading.value = false
      }
    }

    const addCover = async (coverId) => {
      isLoading.value = true
      try {
        await store.dispatch('artists/addTrackCover', {id: trackId(), cover_id: coverId})
        searchCoverResults.value = searchCoverResults.value.filter(t => t.id !== coverId)
        await fetchCovers()
        toast({
          component: ToastificationContent,
          props: {
            title: 'Кавер добавлен',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка добавления кавера',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoading.value = false
      }
    }

    const removeCover = async (coverId) => {
      isLoading.value = true
      try {
        await store.dispatch('artists/removeTrackCover', {id: trackId(), cover_id: coverId})
        await fetchCovers()
        toast({
          component: ToastificationContent,
          props: {
            title: 'Кавер удалён',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка удаления кавера',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoading.value = false
      }
    }

    const addTrackAndCover = async (shsItem) => {
      isLoading.value = true
      try {
        const res = await store.dispatch('artists/addTrack', {
          id: shsItem.performer.id,
          queryParams: { title: shsItem.title },
        })
        const newTrackId = res.data.track_id
        if (shsItem.is_original) {
          await store.dispatch('artists/setTrackOriginal', {id: trackId(), original_id: newTrackId})
        } else {
          const shsOriginal = shsCovers.value.find(c => c.is_original && c.track_id)
          const parentId = shsOriginal ? shsOriginal.track_id : trackId()
          await store.dispatch('artists/addTrackCover', {id: parentId, cover_id: newTrackId})
        }
        shsItem.track_id = newTrackId
        await fetchCovers()
        toast({
          component: ToastificationContent,
          props: {
            title: shsItem.is_original ? 'Трек создан и установлен как оригинал' : 'Трек создан и добавлен как кавер',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка добавления трека/кавера',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoading.value = false
      }
    }

    fetchCovers()

    return {
      trackData,
      originalData,
      coversData,
      isLoading,
      tableColumns,
      formatNumber,
      searchOriginalQuery,
      searchOriginalResults,
      searchForOriginal,
      setOriginal,
      removeOriginal,
      searchCoverQuery,
      searchCoverResults,
      searchForCover,
      addCover,
      removeCover,
      addTrackAndCover,
      isCoverLinked,
      shsCovers,
      shsCoversError,
      shsTableColumns,
      fetchShsCovers,
      isCurrentTrack,
      shsRowClass,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-blog.scss';
</style>
